/**
 * ---------------------------------------------------------------------------------------------------------------------
 * INFINITE SCROLL
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Progressive enhancement that replaces the numbered pagination of the collection (and search) results by an automatic
 * loading of the next pages as the visitor scrolls.
 *
 * Design notes:
 *
 * - The server keeps rendering one page at a time. The first page is therefore identical to what it was before, which
 *   keeps the first paint, the crawl budget and the paginated URLs (`?page=N`) intact.
 * - The "Load more" anchor rendered by the `infinite-scroll` snippet is a real link to the next paginated URL. It stays
 *   in the HTML (so crawlers and no-JS visitors can follow it) and is only hidden once this script takes over.
 * - Products are appended, never re-rendered, so the scroll position of the visitor is never altered.
 * - Exactly one batch is loaded per approach of the end of the list, so the visitor always sees the loader between two
 *   batches instead of the whole collection landing at once.
 * - The loader is both the feedback and the trigger: it sits right under the grid, a batch starts when it comes on
 *   screen, and no batch ever lands before it has been shown for `LOADER_MIN_DISPLAY`.
 * - Every batch is the size the merchant configured. A page rendered by the server rarely holds a whole number of
 *   batches, so a batch that runs out of cards fetches the next page and finishes itself with the cards from it: the
 *   visitor never gets a short batch in the middle of a collection.
 * - A page rendered by the server can hold more cards than the visitor should be shown at once: a product whose
 *   variants are split fills one card per color. The section holds those extra cards back (`data-pending-reveal`) and
 *   they are revealed batch by batch here, before the next page is ever requested. "Products per page" is therefore a
 *   number of CARDS on screen, whatever the number of colors behind them.
 * - The browsing position (last page loaded + scroll offset + last clicked product) is stored in `sessionStorage` and
 *   restored when the visitor comes back from a product page.
 * - The only visible feedback is the loader (the theme `loader-dots` component). The textual progress is kept in a
 *   visually hidden `role="status"` element for screen readers.
 *
 * Events dispatched (bubbling, useful for third party apps that need to hydrate the new cards):
 *
 * - `infinite-scroll:appended` -> { page, items }
 * - `infinite-scroll:completed`
 */

const STORAGE_PREFIX = 'theme:collection-position:';
const STORAGE_TTL = 30 * 60 * 1000; // A position is only restored for 30 minutes
const MAX_STORED_ENTRIES = 8; // Keep the session storage small
const RESTORE_CONCURRENCY = 6; // How many pages are re-fetched at once when restoring a position
const MAX_CONSECUTIVE_ERRORS = 2; // Stop auto-loading (and offer a manual retry) after two failures in a row
const LOADER_MIN_DISPLAY = 700; // No batch ever lands before the loader has been on screen for this long
const TRIGGER_MARGIN = 0; // The loader has to be on screen for a batch to start, so the visitor always sees it
const BATCH_COOLDOWN = 900; // Two batches never run back to back: each one has to be seen arriving on its own

/**
 * Session storage key of the current results set. The `page` parameter is stripped so that the key is shared by all the
 * pages of a same list, while the filters and the sort order are part of the key: a different filter set is a different
 * list, and must therefore never restore the position of another one.
 */
function getStorageKey() {
  const url = new URL(window.location.href);

  ['page', 'section_id'].forEach((parameter) => url.searchParams.delete(parameter));
  url.searchParams.sort();

  return `${STORAGE_PREFIX}${url.pathname}${url.search}`;
}

function readPosition() {
  try {
    const rawState = sessionStorage.getItem(getStorageKey());

    if (!rawState) {
      return null;
    }

    const state = JSON.parse(rawState);

    if (!state || typeof state !== 'object' || Date.now() - (state.timestamp || 0) > STORAGE_TTL) {
      return null;
    }

    return state;
  } catch (error) {
    return null; // Private mode, disabled storage or corrupted entry
  }
}

function writePosition(state) {
  try {
    sessionStorage.setItem(getStorageKey(), JSON.stringify({ ...state, timestamp: Date.now() }));
    prunePositions();
  } catch (error) {
    // Safari in private mode does not allow setting item, we silently fail
  }
}

function clearPosition() {
  try {
    sessionStorage.removeItem(getStorageKey());
  } catch (error) {
    // Silently fail
  }
}

function prunePositions() {
  try {
    const entries = [];

    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index);

      if (!key || !key.startsWith(STORAGE_PREFIX)) {
        continue;
      }

      let timestamp = 0;

      try {
        timestamp = JSON.parse(sessionStorage.getItem(key)).timestamp || 0;
      } catch (error) {
        timestamp = 0;
      }

      entries.push({ key, timestamp });
    }

    entries
      .sort((a, b) => b.timestamp - a.timestamp)
      .forEach(({ key, timestamp }, index) => {
        if (index >= MAX_STORED_ENTRIES || Date.now() - timestamp > STORAGE_TTL) {
          sessionStorage.removeItem(key);
        }
      });
  } catch (error) {
    // Silently fail
  }
}

/**
 * A position is only restored when the visitor is actually coming back to the list: a back/forward navigation, a reload,
 * or a regular navigation coming from a product page (a visitor using a "back to collection" link, for instance).
 */
function shouldRestorePosition() {
  try {
    const navigation = performance.getEntriesByType('navigation')[0];

    if (navigation && (navigation.type === 'back_forward' || navigation.type === 'reload')) {
      return true;
    }

    if (document.referrer) {
      const referrer = new URL(document.referrer, window.location.origin);

      if (referrer.origin === window.location.origin && referrer.pathname.includes('/products/')) {
        return true;
      }
    }
  } catch (error) {
    // Silently fail
  }

  return false;
}

class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.listId = this.getAttribute('list-id');
    this.productList =
      document.getElementById(this.listId) ||
      this.closest('.shopify-section')?.querySelector('.collection__results product-list');

    if (!this.productList) {
      return; // Nothing to append to: the server rendered "Load more" link stays visible and keeps working as a link
    }

    this.sectionId = this.getAttribute('section-id');
    this.revealBatch = Math.max(parseInt(this.getAttribute('reveal-batch'), 10) || 0, 1);
    this.loaderElement = this.querySelector('[data-loader]');
    this.statusElement = this.querySelector('[data-status]');

    this._abortController = null;
    this._observer = null;
    this._isLoading = false;
    this._isCompleted = this.totalPages <= 1 && this.pendingItems.length === 0;
    this._errorCount = 0;
    this._canLoad = true; // Latch: one batch per approach of the end of the list, never a cascade
    this._lastBatchAt = 0;
    this._lastScrollY = window.scrollY;
    this._lastClickedHandle = null;
    this._serverPage = this.currentPage; // Captured before anything appends to the list
    this._listLiveValue = this.productList.getAttribute('aria-live'); // Captured before we ever mute it
    this._unmuteTimer = null;

    // A position is only restored on the very first connection: a filter or sort update re-renders the section from
    // scratch and must always start back at page 1.
    const savedPosition = InfiniteScroll.hasRestored ? null : readPosition();
    const canRestore = Boolean(savedPosition) && savedPosition.startPage === this._serverPage && shouldRestorePosition();

    this._isRestoring = canRestore;

    this.classList.add('infinite-scroll--enhanced');

    this._onProductListClick = this._onProductListClick.bind(this);
    this._onLoadMoreClick = this._onLoadMoreClick.bind(this);
    this._onWindowScroll = this._onWindowScroll.bind(this);
    this._onPageHide = this._onPageHide.bind(this);
    this._onPageShow = this._onPageShow.bind(this);
    this._onUserScrollIntent = this._onUserScrollIntent.bind(this);

    this.productList.addEventListener('click', this._onProductListClick);
    this.addEventListener('click', this._onLoadMoreClick);
    window.addEventListener('scroll', this._onWindowScroll, { passive: true });
    window.addEventListener('pagehide', this._onPageHide);
    window.addEventListener('pageshow', this._onPageShow);

    this._observe();
    this._updateStatus();

    if (canRestore) {
      InfiniteScroll.hasRestored = true;
      this._restorePosition(savedPosition);
    } else if (savedPosition && !InfiniteScroll.hasRestored) {
      clearPosition(); // Fresh visit to the list: forget where we were the last time
    }
  }

  disconnectedCallback() {
    this._abort();
    clearTimeout(this._continuationTimer);

    if (this._scrollFrame) {
      cancelAnimationFrame(this._scrollFrame);
      this._scrollFrame = null;
    }

    if (this._unmuteTimer) {
      clearTimeout(this._unmuteTimer);
      this._unmuteTimer = null;
      this.productList?.setAttribute('aria-live', this._listLiveValue);
    }

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    this.productList?.removeEventListener('click', this._onProductListClick);
    this.removeEventListener('click', this._onLoadMoreClick);
    window.removeEventListener('scroll', this._onWindowScroll);
    window.removeEventListener('pagehide', this._onPageHide);
    window.removeEventListener('pageshow', this._onPageShow);
    window.removeEventListener('wheel', this._onUserScrollIntent);
    window.removeEventListener('touchstart', this._onUserScrollIntent);
    window.removeEventListener('keydown', this._onUserScrollIntent);
  }

  get currentPage() {
    return parseInt(this.getAttribute('data-current-page'), 10) || 1;
  }

  get totalPages() {
    return parseInt(this.getAttribute('data-total-pages'), 10) || 1;
  }

  get totalItems() {
    return parseInt(this.getAttribute('data-total-items'), 10) || 0;
  }

  get nextLink() {
    return this.querySelector('[data-next-link]');
  }

  /**
   * Number of PRODUCTS currently in the list, which is what the pagination counts. A collection rendering its variants
   * as separate products has several cards per product, so the cards are de-duplicated by handle here — otherwise the
   * "Showing X of Y products" status would announce more products than the collection holds.
   */
  get renderedCount() {
    const handles = new Set();

    this.productList
      .querySelectorAll('product-card[handle]:not([data-pending-reveal])')
      .forEach((card) => handles.add(card.getAttribute('handle')));

    return handles.size;
  }

  /** Number of CARDS on screen, which is what a batch is counted in. */
  get visibleCardCount() {
    return this.productList.querySelectorAll('product-card:not([data-pending-reveal])').length;
  }

  /** The cards already fetched that are waiting their turn to be shown, in document order. */
  get pendingItems() {
    return Array.from(this.productList.children).filter((child) => child.hasAttribute('data-pending-reveal'));
  }

  /** How many CARDS are queued up, which is what a batch is measured in. */
  get pendingCardCount() {
    return this.productList.querySelectorAll('product-card[data-pending-reveal]').length;
  }

  // -------------------------------------------------------------------------------------------------------------------
  // LOADING
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * The loader is what is observed, with no margin at all: a batch must not start before the visitor can see the loader,
   * otherwise a slow scroll finishes the batch while the loader is still below the fold and the products simply appear.
   */
  _observe() {
    if (!this.loaderElement || this._isCompleted || !('IntersectionObserver' in window)) {
      return;
    }

    // A small margin, so the batch starts as the visitor arrives rather than after they have hit the wall. The loader
    // pins itself to the screen while it runs, so it is seen either way.
    this._observer = new IntersectionObserver(this._onLoaderIntersect.bind(this), { rootMargin: `${TRIGGER_MARGIN}px 0px` });
    this._observer.observe(this.loaderElement);
  }

  _onLoaderIntersect(entries) {
    // Moving away from the end of the list is what allows the next batch: one batch per approach.
    if (!entries[entries.length - 1].isIntersecting) {
      this._canLoad = true;
      return;
    }

    this._considerLoading();
  }

  /** The single gate every automatic batch goes through. */
  _considerLoading() {
    if (!this._canLoad || this._isLoading || this._isRestoring || this._isCompleted) {
      return;
    }

    if (this._errorCount >= MAX_CONSECUTIVE_ERRORS) {
      return; // The retry link is showing: only an explicit tap resumes the automatic loading
    }

    if (!this._isEndReached()) {
      return;
    }

    this._canLoad = false;
    this._lastBatchAt = Date.now();

    this.loadNextBatch();
  }

  /**
   * A visitor who is still standing at the end of the list once the cooldown has run gets the next batch on its own.
   * Two things need this: the browser keeps a visitor pinned at the very bottom of the document, where there is no
   * scrolling left to do to ask for more; and a batch shorter than the observed margin leaves the end of the list in
   * reach, which the IntersectionObserver has no new event to report.
   *
   * Because it always goes through the same gate, the batches come one at a time, a cooldown apart, each one seen
   * arriving behind the loader — never the whole collection at once. Scrolling away stops them.
   */
  _scheduleContinuation() {
    clearTimeout(this._continuationTimer);

    this._continuationTimer = setTimeout(() => {
      if (this._isCompleted || this._isLoading || this._isRestoring) {
        return;
      }

      this._canLoad = true;
      this._considerLoading();
    }, BATCH_COOLDOWN);
  }

  /**
   * True once the visitor has reached the end of what is loaded — or scrolled past it, which is what happens when they
   * jump straight into the footer.
   */
  /**
   * True while the loader is on the visitor's screen — the only moment a batch may start, so that products never appear
   * without the loader that brought them having been seen.
   *
   * The one exception is a visitor who scrolled straight past the loader into the footer and has nowhere left to go:
   * they cannot bring it back on screen by scrolling down, and stranding them would be worse than a batch they did not
   * watch arrive.
   */
  _isEndReached() {
    if (!this.loaderElement) {
      return false;
    }

    const { top, bottom } = this.loaderElement.getBoundingClientRect();

    if (top - window.innerHeight <= TRIGGER_MARGIN && bottom > 0) {
      return true;
    }

    const atMaxScroll = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    return bottom <= 0 && atMaxScroll;
  }

  _onLoadMoreClick(event) {
    if (!event.target.closest('[data-next-link]')) {
      return;
    }

    event.preventDefault();

    this._errorCount = 0; // Tapping the retry link re-enables the automatic loading
    this.classList.remove('infinite-scroll--errored');
    this.loadNextBatch();
  }

  /**
   * Shows the next batch of cards the section held back. No request is made, and yet the loader is still shown for a
   * moment: the visitor scrolled expecting more products, and the feedback has to read the same whether the cards come
   * from the page they already have or from the next one.
   */
  async loadNextBatch() {
    if (this._isLoading || this._isCompleted) {
      return;
    }

    this._isLoading = true;
    this._setBusy(true);

    // The minimum display and the requests run together, so a fast response never makes the loader blink
    const hold = this._holdLoader();
    let failed = false;

    try {
      // A page rendered by the server rarely ends on a batch boundary. When the queue runs short, the next page is
      // fetched and the batch finishes itself from it: every batch is the size the merchant configured, and only the
      // very last one of the collection is ever smaller.
      while (this.pendingCardCount < this.revealBatch && this.nextLink) {
        const cardsBefore = this.pendingCardCount;
        const linkBefore = this.nextLink.getAttribute('href');

        await this._fetchNextPage();

        if (this.pendingCardCount === cardsBefore && this.nextLink?.getAttribute('href') === linkBefore) {
          break; // That page brought nothing new, stop rather than ask for it again
        }
      }

      this._errorCount = 0;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this._errorCount += 1;
        failed = true;
      }
    }

    await hold;

    const items = this._reveal(this.revealBatch);

    this.setAttribute('data-items-shown', String(this.renderedCount));
    this._savePosition();

    this._isLoading = false;
    this._setBusy(false);
    this._lastBatchAt = Date.now(); // The cooldown runs from the end of the batch, not from its start

    if (failed) {
      this._showError();
    }

    if (items.length > 0) {
      this.dispatchEvent(
        new CustomEvent('infinite-scroll:appended', {
          bubbles: true,
          detail: { page: this.currentPage, items }
        })
      );
    }

    if (this.pendingCardCount === 0 && !this.nextLink) {
      this._complete();
    } else {
      this._scheduleContinuation();
    }
  }

  /** Fetches one more page and queues its cards up, without showing any of them yet. */
  async _fetchNextPage() {
    const link = this.nextLink;

    if (!link) {
      return;
    }

    const sectionDocument = await this._fetchSection(link.getAttribute('href'), this._createAbortSignal());

    this._appendFrom(sectionDocument);
    this._syncNextLink(sectionDocument);

    // The next link now points to the page following the one just fetched
    const fetchedPage = this.nextLink ? this._pageOf(this.nextLink.getAttribute('href')) - 1 : this.totalPages;

    this.setAttribute('data-current-page', String(Math.max(fetchedPage, this.currentPage)));
  }

  /**
   * Keeps the loader on screen long enough to be read. The wait starts on the frame after the loader was told to show
   * itself, so its fade in is not taken out of the time the visitor actually sees it.
   */
  _holdLoader() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => setTimeout(resolve, LOADER_MIN_DISPLAY));
    });
  }

  /**
   * Reveals `count` CARDS. Anything that is not a card (a promotion block) is carried along with the card it was
   * rendered next to, so the grid never reorders itself.
   */
  _reveal(count) {
    const items = [];
    let revealed = 0;

    for (const child of this.pendingItems) {
      child.removeAttribute('data-pending-reveal');

      if (!this._isRestoring) {
        child.classList.add('infinite-scroll-item'); // Cards fade in when they are shown, not when they are fetched
      }

      items.push(child);

      if (child.tagName === 'PRODUCT-CARD' && (revealed += 1) >= count) {
        break;
      }
    }

    return items;
  }

  /**
   * Reveals whatever it takes to get back to the number of cards the visitor had in front of them. The pages have been
   * re-fetched by then, so everything to reveal is already in the list.
   */
  _revealUpTo(cardCount) {
    while (this.visibleCardCount < cardCount && this.pendingItems.length > 0) {
      this._reveal(this.revealBatch);
    }

    this.setAttribute('data-items-shown', String(this.renderedCount));
    this._updateStatus();
  }

  _createAbortSignal() {
    this._abort();
    this._abortController = new AbortController();

    return this._abortController.signal;
  }

  _abort() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  /**
   * Only the section is requested (instead of the whole document), which keeps the payload minimal.
   */
  async _fetchSection(href, signal) {
    const url = new URL(href, window.location.origin);

    url.searchParams.set('section_id', this.sectionId);

    const response = await fetch(url.toString(), { signal, credentials: 'same-origin' });

    if (!response.ok) {
      throw new Error(`Infinite scroll: unexpected response status ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * Identity of a card inside the list. A collection rendering its variants as separate products has several cards
   * for the same product handle, so the key of a card is its `card-key` attribute (handle + variant) whenever it has
   * one, and its handle otherwise.
   */
  _keyOf(card) {
    return card.getAttribute('card-key') || card.getAttribute('handle');
  }

  /**
   * Appends the items of a fetched section into the current list, skipping anything already rendered. Products are
   * identified by their card key and promotional blocks by their ID, which makes duplicates impossible even if the
   * underlying collection changed between two requests.
   */
  _appendFrom(sectionDocument) {
    const sourceList =
      sectionDocument.getElementById(this.listId) || sectionDocument.querySelector('.collection__results product-list');

    if (!sourceList) {
      return [];
    }

    const renderedKeys = new Set(
      Array.from(this.productList.querySelectorAll('product-card')).map((card) => this._keyOf(card))
    );

    const items = [];

    Array.from(sourceList.children).forEach((node) => {
      if (node.tagName === 'PRODUCT-CARD') {
        const key = this._keyOf(node);

        if (!key || renderedKeys.has(key)) {
          return;
        }

        renderedKeys.add(key);
      } else if (node.id && document.getElementById(node.id)) {
        return; // Promotional block already part of the grid
      }

      items.push(document.importNode(node, true));
    });

    if (items.length === 0) {
      return items;
    }

    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      // Images of the appended pages are always lazy, whatever their position inside their own page
      item.querySelectorAll('img[loading="eager"]').forEach((image) => image.setAttribute('loading', 'lazy'));

      // Everything fetched joins the queue: what the visitor sees is decided by the batches, not by the page the
      // server happened to cut at.
      item.setAttribute('data-pending-reveal', '');

      fragment.appendChild(item);
    });

    this._muteList();
    this.productList.appendChild(fragment);

    return items;
  }

  _syncNextLink(sectionDocument) {
    const sourceLink = sectionDocument.querySelector('[data-next-link]');
    const link = this.nextLink;

    if (!link) {
      return;
    }

    if (sourceLink) {
      link.setAttribute('href', sourceLink.getAttribute('href'));
    } else {
      link.remove();
    }
  }

  _pageOf(href) {
    return parseInt(new URL(href, window.location.origin).searchParams.get('page'), 10) || 1;
  }

  _complete() {
    if (this._isCompleted) {
      return;
    }

    this._isCompleted = true;
    clearTimeout(this._continuationTimer);
    this.classList.add('infinite-scroll--completed');
    this.classList.remove('infinite-scroll--errored');
    this.nextLink?.remove();

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    this._updateStatus();
    this.dispatchEvent(new CustomEvent('infinite-scroll:completed', { bubbles: true }));
  }

  _setBusy(isBusy) {
    // `aria-busy` is what reveals the loader and starts its animation, see the styles in the snippet
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (isBusy) {
      this.classList.remove('infinite-scroll--errored');
      this._setStatus(this.getAttribute('data-loading-text'));
    } else {
      this._updateStatus();
    }
  }

  _showError() {
    this.classList.add('infinite-scroll--errored');
    this._setStatus(this.getAttribute('data-error-text'));
  }

  _updateStatus() {
    if (this._isCompleted) {
      if (this.totalPages > 1) {
        this._setStatus(this.getAttribute('data-end-text'));
        return;
      }

      if (this.totalPages <= 1) {
        this._setStatus(''); // A single page never had a pagination to begin with
        return;
      }
    }

    const template = this.getAttribute('data-showing-template');

    if (template) {
      this._setStatus(
        template.replace('__SHOWN__', String(this.renderedCount)).replace('__TOTAL__', String(this.totalItems))
      );
    }
  }

  /**
   * The product list is an aria-live region (the theme uses it for the faceting). Appending a whole page into it would
   * make a screen reader read every single card out loud, so the region is muted around the mutation: the concise
   * "Showing X of Y products" status of this component is the announcement instead.
   *
   * The original value is the one captured on connection, never the current one: several pages are appended in a row
   * when a position is restored, and re-reading the attribute would "restore" the region to `off` for good.
   */
  _muteList() {
    if (!this._listLiveValue) {
      return;
    }

    this.productList.setAttribute('aria-live', 'off');

    // Rescheduled on every append, so the region only comes back once the appends have settled. A timer (rather than
    // an animation frame) keeps this reliable even when the page is not painting.
    clearTimeout(this._unmuteTimer);

    this._unmuteTimer = setTimeout(() => {
      this._unmuteTimer = null;
      this.productList.setAttribute('aria-live', this._listLiveValue);
    }, 100);
  }

  /** The status is visually hidden (`sr-only`): the loader is the only visible feedback. */
  _setStatus(text) {
    if (this.statusElement && typeof text === 'string') {
      this.statusElement.textContent = text;
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // POSITION SAVING / RESTORING
  // -------------------------------------------------------------------------------------------------------------------

  _onWindowScroll() {
    this._lastScrollY = window.scrollY;

    if (this._scrollFrame) {
      return; // At most one evaluation per frame
    }

    this._scrollFrame = requestAnimationFrame(() => {
      this._scrollFrame = null;

      // A visitor sitting at the very bottom is kept there by the scroll anchoring of the browser: the loader never
      // leaves the viewport, the observer stops reporting, and without this nothing would ever load again.
      // Scrolling further is what asks for the next batch — and never before the cooldown, so two batches can never
      // run back to back and every one of them is seen arriving behind the loader.
      if (!this._canLoad && Date.now() - this._lastBatchAt >= BATCH_COOLDOWN) {
        this._canLoad = true;
      }

      this._considerLoading();
    });
  }

  _onProductListClick(event) {
    const link = event.target.closest('a[href]');

    if (!link || !link.href.includes('/products/')) {
      return;
    }

    const clickedCard = link.closest('product-card');

    this._lastClickedHandle = clickedCard ? this._keyOf(clickedCard) : null;
    this._lastScrollY = window.scrollY;
    this._savePosition();
  }

  _onPageHide() {
    this._savePosition();
  }

  /**
   * Safety net for back/forward cache restores (mostly iOS Safari): the document comes back untouched, but if the
   * browser did not restore the scroll offset because we asked for a manual restoration, we do it ourselves.
   */
  _onPageShow(event) {
    if (!event.persisted) {
      return;
    }

    const savedPosition = readPosition();

    if (window.scrollY === 0 && savedPosition && savedPosition.scrollY > 0) {
      window.scrollTo(0, savedPosition.scrollY);
    }
  }

  _savePosition() {
    writePosition({
      startPage: this._serverPage,
      page: this.currentPage,
      scrollY: Math.round(this._lastScrollY),
      handle: this._lastClickedHandle,
      count: this.renderedCount,
      cards: this.visibleCardCount
    });
  }

  async _restorePosition(state) {
    // If the visitor scrolls or interacts while we are restoring, we stop fighting them
    window.addEventListener('wheel', this._onUserScrollIntent, { passive: true, once: true });
    window.addEventListener('touchstart', this._onUserScrollIntent, { passive: true, once: true });
    window.addEventListener('keydown', this._onUserScrollIntent, { once: true });

    const targetPage = Math.min(state.page || 1, this.totalPages);

    this.classList.add('infinite-scroll--restoring');

    if (targetPage > this.currentPage) {
      this._setBusy(true);

      try {
        await this._loadPagesUpTo(targetPage);
      } catch (error) {
        // Even a partial restore is better than losing the position, so we keep going
      }

      this._setBusy(false);
    }

    this._revealUpTo(state.cards || 0);

    this._isRestoring = false;
    this.classList.remove('infinite-scroll--restoring');

    this._scrollTo(state);

    this._canLoad = true;
    this._lastBatchAt = Date.now();
  }

  async _loadPagesUpTo(targetPage) {
    const signal = this._createAbortSignal();
    const pages = [];

    for (let page = this.currentPage + 1; page <= targetPage; page++) {
      pages.push(page);
    }

    // The visitor is waiting in front of a list that is not theirs yet, so the pages are fetched in parallel — but a
    // few at a time, because there is no limit any more to how deep they may have scrolled.
    for (let index = 0; index < pages.length; index += RESTORE_CONCURRENCY) {
      const batch = pages.slice(index, index + RESTORE_CONCURRENCY);

      const sectionDocuments = await Promise.all(
        batch.map((page) => {
          const pageUrl = new URL(window.location.href);

          pageUrl.searchParams.set('page', String(page));

          return this._fetchSection(pageUrl.toString(), signal).catch(() => null);
        })
      );

      sectionDocuments.forEach((sectionDocument) => {
        if (!sectionDocument) {
          return;
        }

        this._appendFrom(sectionDocument);
        this._syncNextLink(sectionDocument);
      });
    }

    this.setAttribute('data-current-page', String(targetPage));
    this.setAttribute('data-items-shown', String(this.renderedCount));
    this._updateStatus();
  }

  _onUserScrollIntent() {
    this._userTookOver = true;
  }

  _scrollTo(state) {
    const scrollToPosition = () => {
      if (this._userTookOver) {
        return;
      }

      const previousBehavior = document.documentElement.style.scrollBehavior;

      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, state.scrollY || 0);

      // Lazy loaded images can shift the grid by a few pixels: if the product the visitor left from is not in the
      // viewport any more, we bring it back instead of trusting the raw offset.
      const card = state.handle
        ? this.productList.querySelector(
            `product-card[card-key="${CSS.escape(state.handle)}"], product-card[handle="${CSS.escape(state.handle)}"]`
          )
        : null;

      if (card) {
        const { top, bottom } = card.getBoundingClientRect();

        if (bottom <= 0 || top >= window.innerHeight) {
          card.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }

      document.documentElement.style.scrollBehavior = previousBehavior;
    };

    // Two frames: the first one lets the appended cards be laid out, the second one measures a settled layout
    requestAnimationFrame(() => requestAnimationFrame(scrollToPosition));

    // Web fonts and above-the-fold images can still move things around until the load event
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => requestAnimationFrame(scrollToPosition), { once: true });
    }
  }
}

InfiniteScroll.hasRestored = false;

if (!window.customElements.get('infinite-scroll')) {
  window.customElements.define('infinite-scroll', InfiniteScroll);
}
